import { TextFieldModule } from '@angular/cdk/text-field';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QRCodeModule } from 'angularx-qrcode';
import { printLocationLabel } from 'app/core/utils/location-label-print';
import {
    EnumViewModel,
    PartLocationDto,
    PartLocationType,
    PartOption,
} from 'app/api/models';
import { PartService } from 'app/api/services';
import { sortOnSelf } from 'app/app.component';
import { SharedModule } from 'app/core/shared.module';
import { isNumber } from 'lodash';
import { of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    switchMap,
} from 'rxjs/operators';

@Component({
    selector: 'part-location-dialog',
    templateUrl: './part-location-dialog.component.html',
    // Standalone because it is opened across lazy-module boundaries: from the Bin & Rack page and
    // from the app shell (classy.component.ts, after a QR scan of a bin). No datepicker in here, so
    // it does not care which DateAdapter the opener provides.
    standalone: true,
    imports: [
        SharedModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatTooltipModule,
        MatRippleModule,
        MatCheckboxModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatOptionModule,
        TextFieldModule,
        MatAutocompleteModule,
        QRCodeModule,
    ],
})
export class PartLocationDialogComponent implements OnInit {
    public form: FormGroup;
    public location: PartLocationDto;

    private allLocations: PartLocationDto[] = [];

    public locationTypes = sortOnSelf(
        Object.values(PartLocationType).filter((x) => isNumber(x)),
        false,
    ).map(
        (x) =>
            <EnumViewModel>{
                name: this.getLocationTypeName(+x),
                value: <PartLocationType>x,
            },
    );
    public parentLocations: PartLocationDto[] = [];

    public PartLocationType = PartLocationType;

    // Free-text label line has to fit the physical 50x30mm label, so cap it.
    public readonly labelTextMaxLength = 40;

    // Multiple parts can live in a single bin. The search box drives an
    // autocomplete; selecting a result moves it into `addedParts`, which is the
    // set persisted (as Part ids) on save. Existing assignments are loaded on
    // open when editing a bin.
    public partSearchCtrl = new FormControl<string>('');
    public partOptions: PartOption[] = [];
    public addedParts: PartOption[] = [];

    public dialogRef = inject<MatDialogRef<PartLocationDialogComponent>>(MatDialogRef);
    private partService = inject(PartService);
    private _formBuilder = inject(FormBuilder);
    private cdr = inject(ChangeDetectorRef);
    public data = inject<{ location: PartLocationDto }>(MAT_DIALOG_DATA);

    ngOnInit(): void {
        this.location = this.data.location;

        this.loadLocations();
        this.buildForm();
        this.setupPartSearch();
        this.loadAssignedParts();
    }

    // Debounced part-number search feeding the autocomplete. Already-added parts
    // are filtered out of the suggestions so they can't be added twice.
    setupPartSearch() {
        this.partSearchCtrl.valueChanges
            .pipe(
                debounceTime(250),
                distinctUntilChanged(),
                switchMap((term) => {
                    var text = (typeof term === 'string' ? term : '').trim();
                    if (!text) return of([] as PartOption[]);

                    return this.partService
                        .apiPartFilteredPartsGet$Json({
                            filterText: text,
                            searchByDescription: false,
                        })
                        .pipe(map((res) => res?.data ?? []));
                }),
            )
            .subscribe((options) => {
                var addedIds = new Set(this.addedParts.map((p) => p.partId));
                this.partOptions = options.filter(
                    (o) => !addedIds.has(o.partId),
                );
                this.cdr.detectChanges();
            });
    }

    // Backfill the parts already stored in this bin when editing an existing one.
    loadAssignedParts() {
        if (!this.location?.partLocationId) return;

        this.partService
            .apiPartLocationPartsGet$Json({
                locationId: this.location.partLocationId,
            })
            .subscribe((res) => {
                this.addedParts = res?.data ?? [];
                this.cdr.detectChanges();
            });
    }

    loadLocations() {
        this.partService
            .apiPartLocationsGet$Json$Response()
            .subscribe((res) => {
                if (!res?.body?.data) return;
                this.allLocations = res.body.data;

                if (this.location) {
                    this.parentLocations = this.allLocations.filter(
                        (x) =>
                            x.locationType ==
                            this.getParentLocationType(
                                this.location.locationType,
                            ),
                    );

                    // When the dialog is opened from the tree view, this.location
                    // is a partial node (PartLocationTreeNodeDto) that omits
                    // labelText/notes/barcode/isWorkshop. Backfill them from the
                    // full record so an edit doesn't blank them — or, in the case
                    // of the workshop flag, silently untick it — on save.
                    // patchValue with emitEvent:false keeps the form pristine
                    // (Save stays disabled until the user actually changes something).
                    var full = this.allLocations.find(
                        (x) =>
                            x.partLocationId == this.location.partLocationId,
                    );
                    if (full)
                        this.form.patchValue(
                            {
                                labelText: full.labelText ?? '',
                                notes: full.notes ?? '',
                                barcode: full.barcode ?? null,
                                isWorkshop: full.isWorkshop ?? false,
                            },
                            { emitEvent: false },
                        );
                }
            });
    }

    buildForm() {
        this.form = this._formBuilder.group({
            // Code is hidden for Bins and auto-filled from the first part number,
            // so it's only user-required for non-Bin location types.
            code: [this.location?.code ?? '', this.requiredUnlessBinValidator()],
            name: [this.location?.name ?? '', Validators.required],
            locationType: [
                this.location?.locationType ?? null,
                Validators.required,
            ],
            barcode: [this.location?.barcode ?? null],
            capacity: [this.location?.capacity ?? null],
            notes: [this.location?.notes ?? ''],
            labelText: [this.location?.labelText ?? ''],
            // Tags this location as belonging to the calibration workshop so it
            // shows up in Form 10a's workshop picker and is kept out of the
            // warehouse stock flows. Defaults off (= warehouse location).
            isWorkshop: [this.location?.isWorkshop ?? false],
            parentLocationId: [
                this.location?.parentLocationId ?? null,
                this.requiredIfNotZoneTypeValidator(),
            ],
        });

        this.cdr.detectChanges();
    }

    requiredUnlessBinValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const parent = control.parent;
            if (!parent) return null;

            const isBin =
                parent.get('locationType')?.value == PartLocationType.Bin;

            return !isBin && !control.value ? { required: true } : null;
        };
    }

    requiredIfNotZoneTypeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const parent = control.parent;
            if (!parent) return null;

            const locationType = parent.get('locationType')?.value;
            const isExempt = locationType == PartLocationType.Warehouse;

            return !isExempt && !control.value ? { required: true } : null;
        };
    }

    locationTypeChanged(event: MatSelectChange) {
        var value = event.value as PartLocationType;

        if (value == PartLocationType.Warehouse) this.parentLocations = [];
        else
            this.parentLocations = this.allLocations.filter(
                (x) => x.locationType == this.getParentLocationType(value),
            );

        // Code's required state depends on whether this is a Bin — re-evaluate it
        // (and the parent validator) now that the type changed.
        this.form.controls['code'].updateValueAndValidity();
        this.form.controls['parentLocationId'].updateValueAndValidity();
    }

    requiresParentId() {
        return (
            this.form?.get('locationType')?.value != PartLocationType.Warehouse
        );
    }

    getParentLocationType(locationType: PartLocationType) {
        switch (locationType) {
            case PartLocationType.Bin:
                return PartLocationType.Shelf;
            case PartLocationType.Shelf:
                return PartLocationType.Rack;
            case PartLocationType.Rack:
                return PartLocationType.Zone;
            case PartLocationType.Zone:
                return PartLocationType.Warehouse;
            case PartLocationType.Warehouse:
                return null;
        }
    }

    getLocationTypeName(locationType: PartLocationType) {
        switch (locationType) {
            case PartLocationType.Bin:
                return 'Bin (Within Shelf)';
            case PartLocationType.Shelf:
                return 'Shelf (Within Rack)';
            case PartLocationType.Rack:
                return 'Rack (Within Zone)';
            case PartLocationType.Zone:
                return 'Zone (Within Warehouse)';
            case PartLocationType.Warehouse:
                return 'Warehouse (Top Level)';
            default:
                return PartLocationType[locationType];
        }
    }

    getFullLocationPath(): string {
        var code = this.form.controls['code'].value;
        if (!code) return '';

        var parentId = this.form.controls['parentLocationId'].value;
        var parts = [code];

        while (parentId) {
            var parent = this.allLocations.find(
                (x) => x.partLocationId == parentId,
            );
            if (!parent) break;
            parts.unshift(parent.code);
            parentId = parent.parentLocationId;
        }

        return parts.join('-');
    }

    // Human-readable label code: just the Rack and Bin, e.g. "R4-L1".
    // The QR still encodes the full path (getFullLocationPath) for uniqueness;
    // only the printed text is shortened.
    getRackBinLabel(): string {
        var rack = this.getCodeForType(PartLocationType.Rack);
        var bin = this.getCodeForType(PartLocationType.Bin);
        return [rack, bin].filter(Boolean).join('-');
    }

    // Returns the code of the ancestor (or current node) of the given type by
    // walking the parent chain and matching on locationType. Codes are
    // free-form (a Bin can be "L1"), so we key off the type, never a prefix.
    private getCodeForType(type: PartLocationType): string {
        if (this.form.controls['locationType'].value == type)
            return this.form.controls['code'].value ?? '';

        var parentId = this.form.controls['parentLocationId'].value;
        while (parentId) {
            var parent = this.allLocations.find(
                (x) => x.partLocationId == parentId,
            );
            if (!parent) break;
            if (parent.locationType == type) return parent.code ?? '';
            parentId = parent.parentLocationId;
        }
        return '';
    }

    // True only for Bin-level locations — parts physically live in bins, so the
    // part-assignment UI is hidden for Racks/Shelves/Zones/Warehouses.
    isBin(): boolean {
        return (
            this.form?.controls['locationType']?.value == PartLocationType.Bin
        );
    }

    // Autocomplete keeps the text box clear after a pick (we render chips below).
    partDisplayWith = (): string => '';

    onPartSelected(event: MatAutocompleteSelectedEvent) {
        var part = event.option.value as PartOption;
        if (!part || this.addedParts.some((p) => p.partId == part.partId))
            return;

        this.addedParts = [...this.addedParts, part];

        // Auto-populate the bin Code from the first part number, but only when
        // Code is still empty so we never clobber a code the user has set.
        var codeCtrl = this.form.controls['code'];
        if (!codeCtrl.value && part.number) {
            codeCtrl.setValue(part.number);
            codeCtrl.markAsDirty();
        }

        // Adding/removing parts must enable Save (form is otherwise pristine).
        this.form.markAsDirty();
        this.partSearchCtrl.setValue('');
        this.partOptions = [];
        this.cdr.detectChanges();
    }

    removePart(partId: number) {
        this.addedParts = this.addedParts.filter((p) => p.partId != partId);
        this.form.markAsDirty();
        this.cdr.detectChanges();
    }

    // Part numbers shown on the label/preview. Capped at 4 — more than that
    // won't physically fit on the 50x30mm label.
    public readonly maxLabelPartNumbers = 4;
    getLabelPartNumbers(): string[] {
        return this.addedParts
            .map((p) => p.number)
            .filter(Boolean)
            .slice(0, this.maxLabelPartNumbers);
    }

    // A single-part bin also shows that part's description; a multi-part bin shows
    // the part numbers only (DLM, 2026-10-15).
    getLabelDescription(): string {
        return this.addedParts.length === 1 ? this.addedParts[0].description?.trim() ?? '' : '';
    }

    // The free-text note is suppressed on the label once a bin holds more than
    // one part — the part numbers take that space instead (per requirement).
    showLabelNote(): boolean {
        return (
            this.addedParts.length <= 1 &&
            !!this.form.controls['labelText'].value
        );
    }

    printQrCode() {
        var printArea = document.getElementById('qr-print-area');
        var fullPath = this.getFullLocationPath();
        if (!printArea || !fullPath) return;

        // Same label as a part's Storage Location field prints (core/utils/location-label-print).
        printLocationLabel({
            qrSvg: printArea.querySelector('qrcode')?.innerHTML ?? '',
            partNumbers: this.addedParts.map((p) => p.number),
            description: this.getLabelDescription(),
            note: this.form.controls['labelText'].value ?? '',
            name: this.form.controls['name'].value ?? '',
            fallbackTitle: fullPath,
        });
    }

    save() {
        // Persist the bin's part assignments alongside the location fields. The
        // backend reconciles Part.LocationId against this id list. Only bins hold
        // parts — for other types send null so the backend skips reconciliation
        // (and never unassigns anything).
        this.dialogRef.close({
            ...this.form.value,
            partIds: this.isBin()
                ? this.addedParts
                      .map((p) => p.partId)
                      .filter((id) => id != null)
                : null,
        });
    }

    close() {
        this.dialogRef.close(null);
    }
}
